/* eslint-disable complexity */
import React, { useEffect, useMemo, useState } from 'react';
import Modal, { Body } from 'src/@vymo/ui/blocks/modal';
import DriverTour from 'src/@vymo/ui/molecules/driver';
import { trackEvent } from 'src/logEvent';
import { setAppHideConfig } from 'src/models/appConfig/slice';
import { ModuleTypeValue } from 'src/modules/types';
import { useAppDispatch } from 'src/store/hooks';
import { ErrorBoundary } from './errorBoundary';
import {
  getModuleById,
  getModulesConfig,
  getStyle,
  setTourShown,
  shouldStartTour,
} from './queries';
import { removeDialog } from './slice';
import { getAppLocationInfo } from './utils';

type Props = {
  id: ModuleTypeValue | 'router';
  isDialog?: Boolean;
  props?: Record<
    string,
    boolean | string | number | ((...args: any) => void) | React.CSSProperties
  >;
};

/**
 * ChildComponent is used to render full page module inside workspace like Manage Channel,Playbook,Sample Module through renderModule
 * ChildComponent is used by module to load nested modules in jsx through <ChildComponent id="SAMPLE" props={}/>.
 */

function ChildComponent({ id, props, isDialog = false }: Props) {
  const dispatch = useAppDispatch();
  const moduleConfig = useMemo(() => getModulesConfig(false), []);
  const { hash, query } = getAppLocationInfo();
  const locationUpdateKey = `${hash}?${query}`;

  const module = useMemo(
    () => getModuleById(id, moduleConfig),
    [id, moduleConfig],
  );

  const [showTour, setShowTour] = useState(false);

  useEffect(() => {
    // Resolve the async `shouldStartTour` and set the state
    const fetchShouldStartTour = async () => {
      const result = await shouldStartTour(
        module?.code,
        module?.helpTour?.start,
      );
      setShowTour(result);
    };

    if (module?.agentic?.summarise) {
      dispatch(setAppHideConfig({ hide: { summarise: false } }));
    } else {
      dispatch(setAppHideConfig({ hide: { summarise: true } }));
    }

    fetchShouldStartTour();
  }, [module?.code, module?.helpTour?.start, module?.agentic, dispatch]);

  useEffect(() => {
    // thunk activate
    module?.thunk?.onActivate?.({
      dispatch,
      props: { ...(props || {}), ...(module.props || {}) },
    });
    trackEvent('Page_Visited', { pageId: module?.code });

    return () => {
      module?.thunk?.onDeactivate?.({
        dispatch,
        props: { ...(props || {}), ...(module.props || {}) },
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, id]);

  useEffect(() => {
    // thunk update
    module?.thunk?.onUpdate?.({
      dispatch,
      props: { ...(props || {}), ...(module.props || {}) },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, id, JSON.stringify(props), locationUpdateKey]);

  const Component: any = module?.component;

  if (isDialog) {
    const style = getStyle(module?.dialog?.styles);
    // TODO :: Add support for Header & Footer in dialog
    return (
      <Modal
        style={style}
        onClose={() =>
          dispatch(
            removeDialog({
              // @ts-ignore
              id,
            }),
          )
        }
        noPadding
        {...(module?.dialog?.props || {})}
      >
        <Body>
          <DriverTour start={showTour} steps={module?.helpTour?.steps || []} />
          <Component {...props} />
        </Body>
      </Modal>
    );
  }
  if (module?.element) {
    const moduleProps = {
      ...module.props,
      ...props,
    };
    return (
      <ErrorBoundary>
        <DriverTour
          start={showTour}
          steps={module?.helpTour?.steps || []}
          onClose={() => setTourShown(module.code)}
        />
        <Component {...moduleProps} />
      </ErrorBoundary>
    );
  }
  return (
    <div>{`Module with id ${id} does not exist. Check in src/module/constants matches the module id ${id} or the module is imported in modulesReducerConfig`}</div>
  );
}

export default React.memo(ChildComponent);
